import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, CalendarDays, X, UserRound, Users } from "lucide-react";
import moment from "moment";

import { getRelistingOpportunities } from "../../../api/dashboard";
import { RelistingRow } from "../../dashboard/components/MarketSignalAlertsPanel";
import ManualSearchOverlay from "../../properties/components/ManualSearchOverlay";
import SuburbChipSearch from "../components/SuburbChipSearch";

/**
 * Re-listing Opportunities (Christian Gravias, Monday 13202980753, 12 Oct 2026).
 *
 * "View All" on the dashboard card lands here, and it has its own Prospecting
 * row between Find Buyers and Garage Sales. It lists every competitor listing
 * that was withdrawn and has not come back on the market, and every listing
 * that has sat 60+ days, from Homer's campaign lists. Agents
 * search by address / suburb / agency, pick a date range, and narrow with the
 * same filter panel the Property Database uses. "Add Lead" (CRM-2026-00114) opens
 * the Add Lead form for the owner; the lead lands on Prospects under New and the
 * property leaves this list.
 *
 * Homer is billed per Listing Record (14 Oct 2026), so super admins see this
 * month's usage against the included allowance above the list.
 *
 * 17 Oct 2026: the search picks several suburbs as chips, like
 * realestate.com.au, and the All / Withdrawn / 60+ counts follow the search.
 *
 * 23 Oct 2026 (Monday 13354971743): the office's Expired listings spreadsheet is
 * imported. Rows carry the owner's name, numbers and email from it, the search
 * finds an owner by name or phone, and "Owner details" shows only rows that have them.
 *
 * 24 Oct 2026 (same item): each row has Find Buyers, and "Best buyer match"
 * lists the properties the most CRM buyers fit first (scored nightly).
 */

const PAGE_SIZE = 50;

const KIND_TABS = [
  { key: "", label: "All", total: "all" },
  { key: "withdrawn", label: "Withdrawn", total: "withdrawn" },
  { key: "stale", label: "60+ days on market", total: "stale" },
];

const SORT_OPTIONS = [
  { key: "newest", label: "Most recent first" },
  { key: "dom", label: "Longest on market" },
  { key: "price_high", label: "Price high to low" },
  { key: "price_low", label: "Price low to high" },
  { key: "suburb", label: "Suburb A to Z" },
  { key: "buyers", label: "Best buyer match" },
];

const EMPTY_ADV = {
  propertyTypes: ["all"],
  priceRange: { min: "Any", max: "Any" },
  bedrooms: { min: "Any", max: "Any" },
  useBedroomRange: false,
  bathrooms: "Any",
  carSpaces: "Any",
  landSize: { min: "Any", max: "Any" },
  saleMethod: "All",
  excludeUnderContract: false,
  targetYield: "Any",
  capitalGrowth: "Any",
  showWithPriceOnly: false,
};

// eslint-disable-next-line no-restricted-syntax -- a count with thousands separators, not a date
const fmt = (n) => Number(n || 0).toLocaleString("en-AU");

// This month's Homer usage. The API only sends it to super admins.
export const HomerUsageStrip = ({ usage }) => {
  if (!usage || !usage.current) return null;
  const cur = usage.current;
  const warn = usage.warn_pct || 80;
  const worst = Math.max(cur.records_pct || 0, cur.metrics_pct || 0);
  const tone =
    worst >= 100
      ? "border-red-200 bg-red-50 text-red-700"
      : worst >= warn
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-gray-200 bg-gray-50 text-gray-600";
  return (
    <div data-testid="homer-usage" className={`mt-3 rounded-md border px-3 py-2 text-[12px] ${tone}`}>
      <span className="font-semibold">Homer usage this month:</span>{" "}
      {fmt(cur.listing_records)} of {fmt(usage.included_listing_records)} Listing Records ({cur.records_pct ?? 0}%),{" "}
      {fmt(cur.metrics_calls)} of {fmt(usage.included_metrics_calls)} Suburb Metrics Calls ({cur.metrics_pct ?? 0}%)
      {cur.estimated_overage > 0 && <span className="font-semibold"> · about ${fmt(cur.estimated_overage)} overage</span>}
    </div>
  );
};

const any = (v) => v === undefined || v === null || v === "" || v === "Any";

// The filter panel's state -> the API's query params.
const toParams = (adv, filters) => {
  const p = {};
  if (!adv.propertyTypes.includes("all")) p.property_type = adv.propertyTypes.join(",");
  if (!any(adv.priceRange.min)) p.price_min = adv.priceRange.min;
  if (!any(adv.priceRange.max)) p.price_max = adv.priceRange.max;
  if (!any(adv.bedrooms.min)) p.beds_min = adv.bedrooms.min;
  if (!any(adv.bedrooms.max)) p.beds_max = adv.bedrooms.max;
  if (!any(adv.bathrooms)) p.baths_min = adv.bathrooms;
  if (!any(adv.carSpaces)) p.cars_min = adv.carSpaces;
  if (!any(adv.landSize.min)) p.land_min = adv.landSize.min;
  if (!any(adv.landSize.max)) p.land_max = adv.landSize.max;
  if (filters.suburb && filters.suburb.trim()) p.suburb = filters.suburb.trim();
  return p;
};

const RelistingOpportunitiesPage = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totals: {}, suburbs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ?q= opens the page on one address (the owner record page's "On Re-listing" link).
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [kind, setKind] = useState("");
  const [sort, setSort] = useState("newest");
  const [suburbs, setSuburbs] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hasOwner, setHasOwner] = useState(false);
  const [page, setPage] = useState(1);

  const [showFilters, setShowFilters] = useState(false);
  const [buyerType, setBuyerType] = useState("owner");
  const [advDraft, setAdvDraft] = useState(EMPTY_ADV);
  const [filtersDraft, setFiltersDraft] = useState({ suburb: "" });
  const [applied, setApplied] = useState({ adv: EMPTY_ADV, filters: { suburb: "" } });

  const params = useMemo(() => {
    const p = { ...toParams(applied.adv, applied.filters), sort, page, page_size: PAGE_SIZE };
    if (query) p.q = query;
    if (kind) p.kind = kind;
    // The suburb chips and the panel's suburb box both narrow by suburb.
    const picked = [p.suburb, ...suburbs].filter(Boolean);
    if (picked.length) p.suburb = picked.join(",");
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (hasOwner) p.has_owner = 1;
    return p;
  }, [applied, sort, page, query, kind, suburbs, dateFrom, dateTo, hasOwner]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getRelistingOpportunities(params);
      setRows(res.data.results || []);
      setMeta(res.data);
    } catch (err) {
      console.error("Error loading re-listing opportunities:", err);
      setError("Could not load re-listing opportunities.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change goes back to page 1.
  useEffect(() => {
    setPage(1);
  }, [applied, sort, query, kind, suburbs, dateFrom, dateTo, hasOwner]);

  const activeFilterCount = Object.keys(toParams(applied.adv, applied.filters)).length;
  const pages = Math.max(1, Math.ceil((meta.count || 0) / PAGE_SIZE));
  const totals = meta.totals || {};
  const staleDays = meta.stale_days || 60;

  const dropRow = (row) => {
    setRows((rs) => rs.filter((r) => r.campaign_id !== row.campaign_id));
    setMeta((m) => ({ ...m, count: Math.max(0, (m.count || 1) - 1) }));
  };

  const clearAll = () => {
    setSearchTerm("");
    setQuery("");
    setKind("");
    setSuburbs([]);
    setDateFrom("");
    setDateTo("");
    setHasOwner(false);
    setAdvDraft(EMPTY_ADV);
    setFiltersDraft({ suburb: "" });
    setApplied({ adv: EMPTY_ADV, filters: { suburb: "" } });
  };
  const anyFilter = query || kind || suburbs.length || dateFrom || dateTo || hasOwner || activeFilterCount > 0;

  return (
    <div className="w-full">
      <div className="px-4 sm:px-6 pt-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Re-listing Opportunities</h1>
            <p className="text-[13px] text-gray-500 mt-0.5">
              Competitor listings withdrawn and not back on the market, listings {staleDays}+ days on market, and the
              office's expired listings, with owner details from our records. Find Buyers shows who in the CRM fits a property; Add Lead puts the owner on Prospects.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-gray-400">
            {meta.last_synced_at && <span>Homer data updated {moment(meta.last_synced_at).fromNow()}</span>}
            <button onClick={load} className="p-1.5 hover:text-teal-600" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
        <HomerUsageStrip usage={meta.homer_usage} />
      </div>

      <div className="px-4 sm:px-6 py-4 w-full overflow-hidden">
        {/* ManualSearchOverlay anchors itself under the ".relative.mb-10" box. */}
        <div className="relative mb-10">
          <SuburbChipSearch
            options={meta.suburb_options || []}
            selected={suburbs}
            onChange={setSuburbs}
            text={searchTerm}
            onTextChange={setSearchTerm}
            onSearch={setQuery}
            onFilterClick={() => setShowFilters(!showFilters)}
            showFilters={showFilters}
            activeFilterCount={activeFilterCount}
          />
          <ManualSearchOverlay
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            buyerType={buyerType}
            setBuyerType={setBuyerType}
            advFilters={advDraft}
            setAdvFilters={setAdvDraft}
            filters={filtersDraft}
            setFilters={setFiltersDraft}
            showBuyerType={false}
            showSaleMethod={false}
            showPriceOnlyToggle={false}
            subtitle="Filter re-listing opportunities."
            onClear={() => {
              setAdvDraft(EMPTY_ADV);
              setFiltersDraft({ suburb: "" });
              setApplied({ adv: EMPTY_ADV, filters: { suburb: "" } });
            }}
            onApply={() => setApplied({ adv: advDraft, filters: filtersDraft })}
          />
        </div>

        {/* Kind tabs + dates and sort */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-fit flex-wrap">
              {KIND_TABS.map((t) => (
                <button
                  key={t.key || "all"}
                  onClick={() => setKind(t.key)}
                  className={`px-3 sm:px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                    kind === t.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t.label}
                  {typeof totals[t.total] === "number" && (
                    <span className="ml-1.5 text-[11px] text-gray-400">{totals[t.total]}</span>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setHasOwner((v) => !v)}
              aria-pressed={hasOwner}
              title="Only properties with an owner name, number or email from the expired listings sheet"
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${
                hasOwner
                  ? "bg-[#55C5D0] border-[#55C5D0] text-white"
                  : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
              }`}
            >
              <UserRound className="w-3.5 h-3.5" />
              Owner details
            </button>
            <button
              type="button"
              onClick={() => setSort((v) => (v === "buyers" ? "newest" : "buyers"))}
              aria-pressed={sort === "buyers"}
              title="Properties with the most buyers who named their suburb come first, then buyers from nearby suburbs. Melbourne-only buyers count as broad matches"
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${
                sort === "buyers"
                  ? "bg-[#55C5D0] border-[#55C5D0] text-white"
                  : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Best buyer match
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
              <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-[12px] text-gray-700 outline-none bg-transparent"
                aria-label="From date"
                title="Withdrawn (or listed) on or after"
              />
              <span className="text-gray-300 text-[12px]">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-[12px] text-gray-700 outline-none bg-transparent"
                aria-label="To date"
                title="Withdrawn (or listed) on or before"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-700"
              aria-label="Sort"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            {anyFilter && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-gray-500 hover:text-red-500"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>

        <div className="mb-2 text-sm text-gray-600">
          Showing <span className="font-bold text-[#55C5D0]">{meta.count || 0}</span> opportunities
          {dateFrom || dateTo ? (
            <span className="text-gray-400"> · dates are the withdrawal date, or the listed date for 60+ day listings</span>
          ) : null}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4">
          {error ? (
            <div className="py-10 text-center text-sm text-red-500">{error}</div>
          ) : loading && rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {meta.last_synced_at
                ? "No opportunities match these filters."
                : "Waiting for the first Homer import. The list fills in after it runs."}
            </div>
          ) : (
            rows.map((row) => (
              <RelistingRow key={row.campaign_id} row={row} staleDays={staleDays} onAssigned={dropRow} />
            ))
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-4 text-[13px] text-gray-600">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            Page {page} of {pages}
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelistingOpportunitiesPage;
